import './instrument';

// i18n
import './locales/i18n';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';

// lightbox
// import 'yet-another-react-lightbox/styles.css';
// import 'yet-another-react-lightbox/plugins/captions.css';
// import 'yet-another-react-lightbox/plugins/thumbnails.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------

import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
// theme
import ThemeProvider from './theme';
// locales
import ThemeLocalization from './locales';
// components
// Straight from the module rather than the barrel beside it, which re-exports
// `react-apexcharts` from its top level. Only charts inside pages need that, and every
// one of those pages loads lazily, so reaching it from here puts apexcharts in the entry
// chunk. `StyledChart` is global styles and depends on nothing else.
import StyledChart from './components/chart/styles';
import SnackbarProvider from './components/snackbar';
import ScrollToTop from './components/scroll-to-top';
import { MotionLazyContainer } from './components/animate';
import { SettingsProvider, ThemeSettings } from './components/settings';
import PageTracker from './components/analytics';
import { LayoutProvider } from './contexts/LayoutContext';
import OrganizationRouter from './sections/auth/OrganizationRouter';
import AppErrorBoundary from './components/error-boundary';

// Check our docs
// https://docs.minimals.cc/authentication/ts-version

// import { AuthProvider } from './auth/JwtContext';

import { Auth0Provider } from '@auth0/auth0-react';
// import { AuthProvider } from './auth/FirebaseContext';
// import { AuthProvider } from './auth/AwsCognitoContext';

import {
  AUTH0_API,
  AUTH0_CACHE_LOCATION,
  INTERCOM_ID,
  ORGANIZATION,
  AUTH0_AUDIENCE,
} from './config';

// Intercom
import { IntercomProvider } from 'react-use-intercom';

// react-query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { OrgContextProvider, OrgSSEBridge } from './contexts/OrgSSEProvider';

const queryClient = new QueryClient();
// ----------------------------------------------------------------------

export default function App() {
  if (!AUTH0_API.domain) {
    throw new Error('Auth0 Domain is not defined');
  }
  if (!AUTH0_API.clientId) {
    throw new Error('Auth0 Client ID is not defined');
  }

  return (
    <Auth0Provider
      domain={AUTH0_API.domain}
      clientId={AUTH0_API.clientId}
      cacheLocation={AUTH0_CACHE_LOCATION}
      authorizationParams={{
        redirect_uri: window.location.origin,
        organization: ORGANIZATION.id,
        audience: AUTH0_AUDIENCE,
      }}
    >
      <IntercomProvider appId={INTERCOM_ID}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <OrgContextProvider>
              <OrgSSEBridge>
                <ReactQueryDevtools initialIsOpen={false} />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <SettingsProvider>
                    <LayoutProvider>
                      <BrowserRouter>
                        <PageTracker />
                        <ScrollToTop />
                        <MotionLazyContainer>
                          <ThemeProvider>
                            <ThemeSettings>
                              <ThemeLocalization>
                                <SnackbarProvider>
                                  <StyledChart />
                                  <AppErrorBoundary>
                                    <OrganizationRouter />
                                  </AppErrorBoundary>
                                </SnackbarProvider>
                              </ThemeLocalization>
                            </ThemeSettings>
                          </ThemeProvider>
                        </MotionLazyContainer>
                      </BrowserRouter>
                    </LayoutProvider>
                  </SettingsProvider>
                </LocalizationProvider>
              </OrgSSEBridge>
            </OrgContextProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </IntercomProvider>
    </Auth0Provider>
  );
}
